/*
 * Presentation metadata for the effective limits.
 *
 * `GET /limits` returns a map keyed by the plan field name, so the keys are the
 * contract's own spelling and this table only decides how each one reads and which
 * usage counter it meters against. Values are never invented here: a limit the API
 * did not send is not rendered, and a limit whose value is `null` is unbounded.
 *
 * The tests worth knowing about, because both are easy to get wrong:
 *  - `null` means unbounded and `0` is a real limit meaning "none allowed", so a
 *    gauge must test for null and never for falsiness;
 *  - `source` decides what a reader can DO about the number — a plan limit is an
 *    upgrade, an `rc` ceiling is a conversation with an operator, an override is
 *    neither, and `unassigned` means no plan is on record so the fail-closed
 *    minimum bound it — which is why provenance is rendered next to every value
 *    rather than hidden behind a tooltip. `floor_value` is an INPUT (the minimum
 *    that was offered), not the outcome: only `source` names the binding layer.
 */
import type { KnowledgeBaseLimit, KnowledgeBaseUsage } from '../../../api/knowledgeBases.ts'

export type LimitUnit = 'count' | 'bytes'

/** Which counter a limit meters against, or `null` for a rate/ceiling with none. */
export type UsageKey = keyof KnowledgeBaseUsage | null

export interface LimitMeta {
  label: string
  unit: LimitUnit
  usage: UsageKey
  /** Extra sentence shown under the gauge where the limit needs one. */
  note?: string
  /** True for limits that count the owner's whole account, not this base. */
  crossBase?: boolean
}

/**
 * Order is the render order: what a base holds, then what the account holds, then
 * the rates, then the retention windows. Ordering by what a reader hits first.
 */
export const LIMIT_META: Record<string, LimitMeta> = {
  max_knowledge_bases: {
    label: 'Базы знаний',
    unit: 'count',
    usage: null,
    crossBase: true,
    note: 'Сколько баз можно держать одновременно.',
  },
  kb_max_raw_files: { label: 'Источников в этой базе', unit: 'count', usage: 'raw_files' },
  kb_max_raw_bytes: { label: 'Вес источников', unit: 'bytes', usage: 'raw_bytes' },
  kb_max_source_file_bytes: {
    label: 'Один файл при загрузке',
    unit: 'bytes',
    usage: null,
    note: 'Максимум на один загружаемый файл.',
  },
  kb_max_wiki_pages: { label: 'Страниц wiki', unit: 'count', usage: 'wiki_pages' },
  kb_max_wiki_bytes: { label: 'Вес wiki', unit: 'bytes', usage: 'wiki_bytes' },
  kb_max_wiki_git_bytes: {
    label: 'Вес git-истории',
    unit: 'bytes',
    usage: 'wiki_git_bytes',
    note: 'История не входит в оплачиваемый объём — показана, чтобы было видно, чего она стоит.',
  },
  kb_total_bytes: {
    label: 'Суммарно по всем базам',
    unit: 'bytes',
    usage: 'account_content_bytes',
    crossBase: true,
  },
  kb_ingests_per_day: {
    label: 'Ingest в сутки',
    unit: 'count',
    usage: null,
    note: 'Скользящие сутки: считаются операции за последние 24 часа, а не с полуночи.',
  },
  kb_ops_per_month: {
    label: 'Операций за месяц',
    unit: 'count',
    usage: null,
    note: 'ingest + query + lint за скользящие 30 дней.',
  },
  kb_max_concurrent_ops: {
    label: 'Параллельных операций',
    unit: 'count',
    usage: null,
    crossBase: true,
    note:
      'Считается по всем вашим базам сразу. Две операции на одну базу — это отдельный механизм: ' +
      'вторая получит wiki только для чтения из-за лизы на запись.',
  },
  kb_git_retention_days: {
    label: 'Хранение git-истории',
    unit: 'count',
    usage: null,
    note: 'Дней, после которых старые коммиты сворачиваются.',
  },
  kb_archive_retention_days: {
    label: 'Хранение архива',
    unit: 'count',
    usage: null,
    note: 'Дней после архивации, пока базу ещё можно вернуть.',
  },
}

export interface LimitSourceLook {
  label: string
  className: string
  /** What the reader can do about it, shown beside the gauge. */
  recourse: string
}

export const LIMIT_SOURCES: Record<string, LimitSourceLook> = {
  plan: {
    label: 'план',
    className: 'src-tag--plan',
    recourse: 'значение из тарифного плана',
  },
  rc: {
    label: 'rc',
    className: 'src-tag--rc',
    recourse: 'платформенный потолок ниже плана',
  },
  override: {
    label: 'override',
    className: 'src-tag--override',
    recourse: 'индивидуальная настройка для этой базы',
  },
  unassigned: {
    label: 'минимум',
    className: 'src-tag--rc',
    recourse: 'тариф не назначен — действует минимальный уровень',
  },
  unbounded: {
    label: 'без лимита',
    className: 'src-tag--unbounded',
    recourse: 'ничто не ограничивает',
  },
}

export function sourceLook(source: string): LimitSourceLook {
  return (
    LIMIT_SOURCES[source] ?? {
      label: source,
      className: 'src-tag--rc',
      recourse: 'источник лимита не распознан',
    }
  )
}

/** Metadata for a key the table does not know, so a new limit still renders. */
export function metaFor(key: string): LimitMeta {
  return LIMIT_META[key] ?? { label: key, unit: 'count', usage: null }
}

/** Render order: known keys in table order, then anything new, alphabetically. */
export function orderedLimitKeys(limits: Record<string, KnowledgeBaseLimit>): string[] {
  const known = Object.keys(LIMIT_META).filter((key) => key in limits)
  const unknown = Object.keys(limits)
    .filter((key) => !(key in LIMIT_META))
    .sort()
  return [...known, ...unknown]
}

/** True when the gauge should read as a warning rather than as information. */
export function isPressing(used: number, limit: number | null | undefined): boolean {
  if (limit === null || limit === undefined || limit <= 0) return false
  return used / limit >= 0.8
}
